import { useCallback, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface BackNavigationOptions {
  /** Default route to navigate to if no previous path is available */
  defaultRoute: string;
  /** Whether to preserve search parameters when navigating back */
  preserveSearchParams?: boolean;
  /** Custom logic to determine if the previous path is valid for back navigation */
  isValidPreviousPath?: (_previousPath: string) => boolean;
  /** Custom error handler for navigation failures */
  onNavigationError?: (_error: Error) => void;
  /** Route patterns that are considered valid source routes for back navigation */
  validSourceRoutes?: string[];
  /** Whether to use browser history for navigation (-1) when valid previous path exists */
  useBrowserHistory?: boolean;
}

// Constants for better performance and maintainability
const DEFAULT_EXCLUDED_PATHS = ['/login', '/logout', '/error', '/404', '/403'] as const;
const EMPTY_SEARCH_PARAMS = '';

export interface BackNavigationReturn {
  /** Navigate back to the previous route or default route */
  goBack: () => void;
  /** Check if back navigation is available (has valid previous path) */
  canGoBack: boolean;
  /** The path that will be navigated to when goBack is called */
  backPath: string;
  /** Whether the user came from a valid previous route */
  hasValidPreviousPath: boolean;
  /** The source route the user came from (if any) */
  sourceRoute: string | null;
  /** Navigation method that will be used (browser history or programmatic) */
  navigationMethod: 'history' | 'programmatic';
}

/**
 * Custom hook for intelligent back navigation with search parameter preservation
 *
 * @param options Configuration options for back navigation behavior
 * @returns Object containing navigation functions and state
 *
 * @example
 * ```tsx
 * // Basic usage
 * const { goBack, canGoBack } = useBackNavigation({
 *   defaultRoute: '/booking-list'
 * });
 *
 * // Advanced usage with validation
 * const { goBack, canGoBack, backPath } = useBackNavigation({
 *   defaultRoute: '/products',
 *   preserveSearchParams: true,
 *   isValidPreviousPath: (path) => path.startsWith('/products'),
 *   onNavigationError: (error) => console.error('Navigation failed:', error)
 * });
 * ```
 */
export const useBackNavigation = (options: BackNavigationOptions): BackNavigationReturn => {
  const {
    defaultRoute,
    preserveSearchParams = true,
    isValidPreviousPath,
    onNavigationError,
    validSourceRoutes,
    useBrowserHistory = true,
  } = options;

  const navigate = useNavigate();
  const location = useLocation();

  // Track navigation history internally with better performance
  const previousPathRef = useRef<string | null>(null);
  const currentPathRef = useRef<string>(location.pathname);
  const isInitialMount = useRef(true);

  // Update path tracking on location change (optimized)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (currentPathRef.current !== location.pathname) {
      previousPathRef.current = currentPathRef.current;
      currentPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Memoized previous path resolution for better performance
  const previousPath = useMemo(() => {
    // Prioritize navigation state over internal tracking
    const statePreviousPath = location.state?.previousPath;
    if (typeof statePreviousPath === 'string' && statePreviousPath.length > 0) {
      return statePreviousPath;
    }

    return previousPathRef.current;
  }, [location.state?.previousPath]);

  // Optimized route pattern matching
  const matchesRoutePattern = useCallback((path: string, pattern: string): boolean => {
    if (pattern.endsWith('*')) {
      return path.startsWith(pattern.slice(0, -1));
    }
    return path === pattern || path.startsWith(pattern + '/');
  }, []);

  // Validate if the previous path is suitable for back navigation (optimized)
  const isValidForBackNavigation = useCallback(
    (path: string | null): boolean => {
      if (!path || path === location.pathname) {
        return false;
      }

      // Use custom validation if provided (early return)
      if (isValidPreviousPath) {
        return isValidPreviousPath(path);
      }

      // Check against valid source routes if provided (optimized)
      if (validSourceRoutes?.length) {
        return validSourceRoutes.some(route => matchesRoutePattern(path, route));
      }

      // Default validation: exclude problematic routes
      return !DEFAULT_EXCLUDED_PATHS.some(excluded => path.startsWith(excluded));
    },
    [location.pathname, isValidPreviousPath, validSourceRoutes, matchesRoutePattern]
  );

  // Extract search parameters efficiently
  const extractSearchParams = useCallback((path: string): string => {
    const searchIndex = path.indexOf('?');
    return searchIndex !== -1 ? path.substring(searchIndex) : EMPTY_SEARCH_PARAMS;
  }, []);

  // Construct the back path with preserved search parameters (optimized)
  const constructBackPath = useCallback(
    (basePath: string, sourcePath?: string): string => {
      if (!preserveSearchParams || !sourcePath) {
        return basePath;
      }

      const searchParams = extractSearchParams(sourcePath);
      return searchParams ? basePath + searchParams : basePath;
    },
    [preserveSearchParams, extractSearchParams]
  );

  // Memoized navigation state analysis (optimized)
  const navigationState = useMemo(() => {
    const hasValidPreviousPath = isValidForBackNavigation(previousPath);
    const effectivePreviousPath = hasValidPreviousPath ? previousPath! : null;

    // Calculate the back path efficiently
    const backPath = hasValidPreviousPath
      ? constructBackPath(previousPath!, previousPath!)
      : constructBackPath(defaultRoute);

    // Determine navigation method
    const navigationMethod: 'history' | 'programmatic' =
      hasValidPreviousPath && useBrowserHistory ? 'history' : 'programmatic';

    return {
      sourceRoute: effectivePreviousPath,
      hasValidPreviousPath,
      backPath,
      navigationMethod,
    };
  }, [previousPath, isValidForBackNavigation, constructBackPath, defaultRoute, useBrowserHistory]);

  // Optimized navigation function with better error handling
  const goBack = useCallback(() => {
    try {
      const { hasValidPreviousPath, backPath, navigationMethod } = navigationState;

      if (hasValidPreviousPath && navigationMethod === 'history') {
        navigate(-1);
      } else {
        navigate(backPath);
      }
    } catch (error) {
      const navigationError = error instanceof Error ? error : new Error('Navigation failed');

      // Handle error gracefully
      if (onNavigationError) {
        onNavigationError(navigationError);
      } else {
        console.error('Back navigation failed:', navigationError);
      }

      // Always provide fallback navigation
      try {
        navigate(defaultRoute);
      } catch (fallbackError) {
        console.error('Fallback navigation also failed:', fallbackError);
      }
    }
  }, [navigationState, navigate, defaultRoute, onNavigationError]);

  return {
    goBack,
    canGoBack: true, // Always true since we have a fallback
    backPath: navigationState.backPath,
    hasValidPreviousPath: navigationState.hasValidPreviousPath,
    sourceRoute: navigationState.sourceRoute,
    navigationMethod: navigationState.navigationMethod,
  };
};

export default useBackNavigation;
